import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  Survey,
  SurveyAnswer,
  SurveyQuestion,
} from '../../entities/survey.entity';
import { SubscriptionHelper } from '../../helpers/subscription.helper';
import { AppPopupService } from '../../services/app-popup.service';
import { SurveysService } from '../../services/survey.service';
import { MessageModalComponent } from '../message-modal/message-modal.component';

@Component({
  selector: 'app-survey-preview',
  templateUrl: './survey-preview.component.html',
  styleUrl: './survey-preview.component.scss',
})
export class SurveyPreviewComponent implements OnDestroy {
  _previewId: string = '';
  @Input() set previewId(value: string) {
    this._previewId = value;
    this.initPreview(value);
  }
  get previewId() {
    return this._previewId;
  }
  surveyData: Survey = {} as Survey;
  progressPercentage = 0;
  answers: SurveyAnswer[] = [];
  errors: string[] = [];
  isFormValid = false;
  isLoading = false;
  isKioskMode = false;

  assets: any = {};
  private objectUrls: string[] = [];

  constructor(
    private spinner: NgxSpinnerService,
    private router: Router,
    private surveyService: SurveysService,
    public dialog: MatDialog,
    private appPopup: AppPopupService,
    private subsMgr: SubscriptionHelper,
    private cdr: ChangeDetectorRef
  ) {}
  ngAfterViewInit(): void {}

  initPreview(previewId: string): void {
    if (previewId) {
      this.loadPreview();
    }
  }
  loadPreview(): void {
    const raw =
      sessionStorage.getItem(this.previewId) ??
      localStorage.getItem(this.previewId);

    if (!raw) {
      console.error('Preview data not found for id:', this.previewId);
      this.appPopup.error(
        'Preview unavailable',
        'Survey data was not found. Please try again.'
      );
      return;
    }

    const data = JSON.parse(raw);
    if (!Array.isArray(data.questions)) {
      console.error('Preview data missing questions:', data);
      this.appPopup.error(
        'Preview unavailable',
        'Survey data is incomplete. Please try again.'
      );
      return;
    }

    this.assets = data.assets ?? {};
    this.handleSurveyData(data);
  }

  loadSurvey(id: string): void {
    this.subsMgr.add(
      this.surveyService.getSurveyById(id).subscribe({
        next: (data) => this.handleSurveyData(data),
        error: (error) => this.handleError(error),
        complete: async () => {
          if (!this.isKioskMode) {
            const progress = await this.surveyService.getSavedInProgressSurvey(
              this.surveyData.eventUUID
            );
            this.answers = progress.answers;
            this.progressPercentage = this.getProgressPercentage();
          } else {
            this.answers = [];
            this.progressPercentage = 0;
          }
          this.assets = this.surveyData.assets;
          this.checkIsFormValid();
          this.cdr.detectChanges();
        },
      })
    );
  }

  getSavedValue(question: SurveyQuestion) {
    const defaultReturnValue = ['ranking', 'multiple-choose'].includes(
      question.type
    )
      ? []
      : '';
    return this.answers
      ? this.answers.find((a) => a.questionId === question.questionId)
          ?.answer || defaultReturnValue
      : defaultReturnValue;
  }

  getFollowUpSavedValue(question: SurveyQuestion) {
    return this.answers
      ? this.answers.find((a) => a.questionId === question.questionId)
          ?.followUpAnswer || ''
      : '';
  }

  handleSurveyData(data: Survey): void {
    const sortedQuestions = [...data.questions].sort(
      (a, b) => a.order - b.order
    );
    this.surveyData = { ...data, questions: sortedQuestions };

    // Migrate legacy sections that have questions array to questionIds
    if (this.surveyData.sections) {
      this.surveyData.sections.forEach((section) => {
        if (
          section.questions &&
          section.questions.length > 0 &&
          (!section.questionIds || section.questionIds.length === 0)
        ) {
          section.questionIds = section.questions.map((q) => q.questionId);
          delete section.questions;
        }
      });
    }

    this.cdr.detectChanges();
  }

  /**
   * Resolves questions for a section by looking up questionIds in the questions array
   * This ensures we always get the latest question data from the source of truth
   */
  getQuestionsForSection(section: any): SurveyQuestion[] {
    if (!section.questionIds || section.questionIds.length === 0) {
      // Fallback to legacy questions array if questionIds doesn't exist
      if (section.questions && section.questions.length > 0) {
        return section.questions;
      }
      return [];
    }

    // Create a map for O(1) lookup
    const questionsMap = new Map<string, SurveyQuestion>();
    this.surveyData.questions.forEach((q) => {
      questionsMap.set(q.questionId, q);
    });

    // Resolve questions in the order specified by questionIds
    const resolvedQuestions: SurveyQuestion[] = [];
    section.questionIds.forEach((questionId: string) => {
      const question = questionsMap.get(questionId);
      if (question) {
        resolvedQuestions.push(question);
      }
    });

    return resolvedQuestions;
  }

  private handleError(error: any): void {
    this.appPopup.error('An Error Ocurr', 'Try again later, Thanks!');
    console.error('Error loading survey:', error);
  }

  submit(): void {}

  downloadSurveyByCode(code: string, type: string) {
    this.surveyService
      .addSurveyByCodePromise(code, type)
      .then((result) => {
        if (result.success) {
          this.loadSurvey(result.eventUUID);
        }
      })
      .catch(() => {
        this.router.navigateByUrl('/public');
      });
  }

  sayThankyou(): void {
    this.dialog.open(MessageModalComponent, {
      width: '200px',
      maxWidth: '98%',
      data: {
        text: 'Thank you for your participation!',
        type: 'success',
        confirm: false,
      },
    });
  }
  validateBackNavigation(): void {
    if (this.isKioskMode) {
      this.router.navigateByUrl('/');
    } else {
      const dialogRef = this.dialog.open(MessageModalComponent, {
        width: '200px',
        maxWidth: '98%',
        data: {
          text: 'Are you sure you want to exit the survey without completing it?',
          type: 'warning',
          confirmText: 'Yes, Sure',
        },
      });

      this.subsMgr.add(
        dialogRef.afterClosed().subscribe((result) => {
          if (result === 'confirm') {
            this.router.navigateByUrl('/public');
          }
        })
      );
    }
  }
  checkIsFormValid() {
    this.errors = [];
    this.surveyData.questions.forEach((q, i) => {
      const answerObject = this.answers
        ? this.answers.find((a) => a.questionId === q.questionId)
        : null;

      let isAnswered = false;
      if (answerObject) {
        if (typeof answerObject.answer === 'string') {
          isAnswered = !!answerObject.answer.trim();
        } else if (Array.isArray(answerObject.answer)) {
          isAnswered = answerObject.answer.every((item) => !!item.trim());
        }
      }

      if (q.isRequired && q.type === 'ranking') {
        isAnswered =
          isAnswered &&
          q.options.length === answerObject?.answer.filter(Boolean).length;
      } else if (
        q.type === 'ranking' &&
        answerObject?.answer.filter(Boolean).length > 0
      ) {
        if (q.options.length !== answerObject?.answer.filter(Boolean).length) {
          this.errors.push(`Question #${i + 1} is incomplete.`);
        }
      }

      if (q.isRequired && !isAnswered) {
        this.errors.push(`Question #${i + 1} is required.`);
      }
    });

    this.isFormValid = this.errors.length === 0;
  }

  async onQuestionValueChanged(value: SurveyAnswer): Promise<void> {
    if (!this.answers) {
      this.answers = [];
    }
    const index = this.answers.findIndex(
      (a) => a.questionId === value.questionId
    );
    if (index >= 0) {
      this.answers[index] = value;
    } else {
      this.answers.push(value);
    }

    if (this.surveyData && this.surveyData.questions) {
      this.progressPercentage = this.getProgressPercentage();

      this.checkIsFormValid();
    }
  }

  getProgressPercentage() {
    if (!this.answers || !this.surveyData?.questions) {
      return 0;
    }

    const validAnswersCount = this.answers.filter(
      (a) => a.answer.length > 0
    ).length;

    return parseFloat(
      ((validAnswersCount / this.surveyData.questions.length) * 100).toFixed(1)
    );
  }

  ngOnDestroy(): void {
    this.subsMgr.destroy();
    this.objectUrls.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    this.objectUrls = [];
    if (this.previewId) {
      sessionStorage.removeItem(this.previewId);
      localStorage.removeItem(this.previewId);
    }
  }
  arrayBufferToImageUrl(arrayBuffer: any) {
    const blob = new Blob([arrayBuffer], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    this.objectUrls.push(url);
    return url;
  }
  getAssets(): any {
    if (!this.surveyData.image) return;

    const key = this.surveyData.image;
    const asset = this.assets[key as string];

    return asset;
  }
}
